import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Grid,
  Box,
  SelectChangeEvent,
  FormHelperText,
  Typography,
} from '@mui/material';
import Button from 'components/CustomButtons/Button.js';
import { useTranslation } from 'react-i18next';
import { Guide, Discord, GuideType, CharacterClass, getAllDiscords } from './GuideApi';

const useStyles = makeStyles<Theme>((theme) => ({
  cardCategoryWhite: {
    color: 'rgba(255,255,255,.62)',
    margin: '0',
    fontSize: '14px',
    marginTop: '0',
    marginBottom: '0',
    textAlign: 'left',
  },
  cardTitleWhite: {
    color: '#FFFFFF',
    marginTop: '0px',
    minHeight: 'auto',
    fontWeight: 300,
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: '3px',
    textDecoration: 'none',
    textAlign: 'left',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    width: '100%',
    textAlign: 'left',
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
  switchLabel: {
    marginLeft: 0,
    textAlign: 'left',
  },
  editorWrapper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'left',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  button: {
    width: '100%',
  },
  descriptionTextarea: {
    width: '100%',
    minHeight: '300px',
    padding: theme.spacing(1),
    fontFamily: 'inherit',
    fontSize: 'inherit',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '&:focus': {
      outlineColor: theme.palette.primary.main,
    },
    textAlign: 'left',
  },
  videoUploadSection: {
    width: '100%',
    textAlign: 'left',
  },
  switchContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
  },
}));

interface GuideFormProps {
  guide?: Guide;
  onSubmit: (guide: Guide) => void;
  onCancel: () => void;
  loading: boolean;
}

const GuideForm: React.FC<GuideFormProps> = ({ guide, onSubmit, onCancel, loading }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [discords, setDiscords] = useState<Discord[]>([]);
  const [loadingDiscords, setLoadingDiscords] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<Partial<Guide>>({
    name: guide?.name || '',
    description: guide?.description || '',
    type: guide?.type || GuideType.GENERAL,
    class: guide?.class || CharacterClass.ANY,
    team_id: guide?.team_id || '',
    team_name: guide?.team_name || '',
    video_url: guide?.video_url || '',
    video_file: undefined,
    published: guide?.published || false,
  });

  useEffect(() => {
    fetchDiscords();
  }, []);

  useEffect(() => {
    if (guide) {
      setFormData({
        ...(guide.id ? { id: guide.id } : {}),
        name: guide.name,
        description: guide.description,
        type: guide.type,
        class: guide.class,
        team_id: guide.team_id,
        team_name: guide.team_name,
        video_url: guide.video_url,
        video_file: undefined,
        published: guide.published,
      });
    }
  }, [guide]);

  const fetchDiscords = async () => {
    setLoadingDiscords(true);
    try {
      const response = await getAllDiscords();
      setDiscords(response);
    } catch (error) {
      console.error('Error fetching discords:', error);
    } finally {
      setLoadingDiscords(false);
    }
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    // Validate team selection
    if (!formData.team_id) {
      setTeamError(t('guides.errors.teamRequired'));
      return;
    }

    const selectedTeam = discords.find((discord) => discord.id === formData.team_id);

    // Check if we have a video file to upload
    if (formData.video_file) {
      // Use FormData for file uploads
      const formDataToSend = new FormData();

      // Add all form fields as strings to ensure proper server parsing
      formDataToSend.append('name', String(formData.name || ''));
      formDataToSend.append('description', String(formData.description || ''));

      // For enum values, ensure we only send the string value not the enum object
      const typeString =
        typeof formData.type === 'string' ? formData.type : String(formData.type || 'general');
      const classString =
        typeof formData.class === 'string' ? formData.class : String(formData.class || 'any');

      formDataToSend.append('type', typeString);
      formDataToSend.append('class', classString);

      // Pass team_id directly without conversion (could be guid format with dashes)
      formDataToSend.append('team_id', formData.team_id);

      formDataToSend.append('team_name', String(selectedTeam?.name || ''));

      // Use JSON.stringify for the published boolean to ensure proper format
      formDataToSend.append('published', JSON.stringify(formData.published || false));

      // Add the video file
      formDataToSend.append('video_file', formData.video_file);

      if (guide?.id) {
        formDataToSend.append('id', String(guide.id));
      }

      // Submit the form with FormData
      onSubmit(formDataToSend as unknown as Guide);
    } else {
      // Use the standard JSON approach for submissions without files
      const guideData = {
        ...formData,
        ...(guide?.id ? { id: guide.id } : {}),
        team_id: formData.team_id,
        team_name: selectedTeam?.name || '',
        created_at: guide?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as Guide;

      onSubmit(guideData);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name === 'team_id') {
      setTeamError(null);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, video_file: e.target.files![0], video_url: '' }));
    }
  };

  return (
    <>
      <form onSubmit={handleFormSubmit} style={{ marginTop: '12px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              label={t('guides.fields.name')}
              fullWidth
              variant="outlined"
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!teamError} required>
              <InputLabel>{t('guides.fields.team')}</InputLabel>
              <Select
                name="team_id"
                value={formData.team_id}
                onChange={handleSelectChange}
                label={t('guides.fields.team')}
                disabled={loading || loadingDiscords}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                  },
                }}
              >
                {discords.map((discord) => (
                  <MenuItem
                    key={discord.id}
                    value={discord.id}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  >
                    {discord.name}
                  </MenuItem>
                ))}
              </Select>
              {loadingDiscords && (
                <FormHelperText>{t('common.loading.fetchingData')}</FormHelperText>
              )}
              {teamError && <FormHelperText error>{teamError}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('guides.fields.type')}</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleSelectChange}
                label={t('guides.fields.type')}
                disabled={loading}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                  },
                }}
              >
                <MenuItem
                  value={GuideType.GENERAL}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.type.general')}
                </MenuItem>
                <MenuItem
                  value={GuideType.PVP}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.type.pvp')}
                </MenuItem>
                <MenuItem
                  value={GuideType.PVM}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.type.pvm')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('guides.fields.class')}</InputLabel>
              <Select
                name="class"
                value={formData.class}
                onChange={handleSelectChange}
                label={t('guides.fields.class')}
                disabled={loading}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                  },
                }}
              >
                <MenuItem
                  value={CharacterClass.ANY}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.class.any')}
                </MenuItem>
                <MenuItem
                  value={CharacterClass.BARBARIAN}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.class.barbarian')}
                </MenuItem>
                <MenuItem
                  value={CharacterClass.SORCERESS}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.class.sorceress')}
                </MenuItem>
                <MenuItem
                  value={CharacterClass.AMAZON}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.class.amazon')}
                </MenuItem>
                <MenuItem
                  value={CharacterClass.ASSASSIN}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.class.assassin')}
                </MenuItem>
                <MenuItem
                  value={CharacterClass.NECROMANCER}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.class.necromancer')}
                </MenuItem>
                <MenuItem
                  value={CharacterClass.PALADIN}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.class.paladin')}
                </MenuItem>
                <MenuItem
                  value={CharacterClass.DRUID}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {t('guides.class.druid')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              label={t('guides.fields.description')}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <div className={classes.videoUploadSection}>
              <Typography variant="body1" gutterBottom align="left">
                {t('guides.fields.videoUpload')}
              </Typography>

              {/* Display current video URL if exists */}
              {formData.video_url && !formData.video_file && (
                <Box mb={2}>
                  <Typography variant="body2" align="left">
                    {t('guides.currentVideo')}: {formData.video_url}
                  </Typography>
                </Box>
              )}

              {/* File upload input */}
              <input
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={loading}
                style={{ marginBottom: '16px', textAlign: 'left' }}
              />

              {/* Show selected file name */}
              {formData.video_file && (
                <Typography variant="body2" color="primary" align="left">
                  {t('guides.selectedFile')}: {formData.video_file.name}
                </Typography>
              )}
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.switchContainer}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.published}
                    onChange={handleSwitchChange}
                    name="published"
                    disabled={loading}
                  />
                }
                label={t('guides.fields.published')}
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" color="primary" disabled={loading} fullWidth={true}>
              {loading ? t('common.saving') : guide?.id ? t('common.update') : t('common.create')}
            </Button>
            <Button color="outlined" onClick={onCancel} fullWidth={true} disabled={loading}>
              {t('common.cancel')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export { GuideForm };
